import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  ScheduleConfig,
  UpdateScheduleRequest,
  UpdateScheduleResponse,
  SchedulePreviewResponse,
} from '../types/pipeline';

const scheduleKeys = {
  all: ['schedules'] as const,
  preview: (cron: string, timezone: string) =>
    [...scheduleKeys.all, 'preview', cron, timezone] as const,
};

/**
 * Hook to update pipeline schedule configuration
 */
export function useUpdatePipelineSchedule(pipelineId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scheduleConfig: ScheduleConfig) => {
      const request: UpdateScheduleRequest = { scheduleConfig };
      return apiClient.put<UpdateScheduleResponse>(
        `/pipelines/${pipelineId}/schedule`,
        request
      );
    },
    onSuccess: () => {
      // Invalidate pipeline queries to refresh schedule data
      queryClient.invalidateQueries({ queryKey: ['pipelines', 'detail', pipelineId] });
      queryClient.invalidateQueries({ queryKey: ['pipelines', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['pipelines', 'status', pipelineId] });
    },
  });
}

/**
 * Hook to delete pipeline schedule
 */
export function useDeletePipelineSchedule(pipelineId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return apiClient.delete(`/pipelines/${pipelineId}/schedule`);
    },
    onSuccess: () => {
      // Invalidate pipeline queries to refresh schedule data
      queryClient.invalidateQueries({ queryKey: ['pipelines', 'detail', pipelineId] });
      queryClient.invalidateQueries({ queryKey: ['pipelines', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['pipelines', 'status', pipelineId] });
    },
  });
}

/**
 * Hook to preview schedule (get next N execution times)
 */
export function useSchedulePreview(cron: string, timezone: string, count: number = 5) {
  return useQuery({
    queryKey: scheduleKeys.preview(cron, timezone),
    queryFn: async () => {
      const params = new URLSearchParams({
        cron,
        timezone,
        count: count.toString(),
      });

      return apiClient.get<SchedulePreviewResponse>(
        `/schedules/preview?${params.toString()}`
      );
    },
    enabled: !!(cron && timezone && cron.split(' ').length === 5),
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}
